import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimeOff = () => {
  const [requests, setRequests] = useState([]);
  const [ptoSummary, setPtoSummary] = useState(null);
  const [ptoLedger, setPtoLedger] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [timeOffType, setTimeOffType] = useState('pto');
  const [paidChoice, setPaidChoice] = useState('paid');   // paid | unpaid (office has final say)
  const [partialDay, setPartialDay] = useState(false);
  const [partialStart, setPartialStart] = useState('13:00');
  const [partialEnd, setPartialEnd] = useState('17:00');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchPto();
  }, []);

  const fetchRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/timeoff?scope=mine`, { withCredentials: true });
      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchPto = async () => {
    try {
      const { data } = await axios.get(`${API}/pto/ledger`, { withCredentials: true });
      setPtoSummary(data.summary);
      setPtoLedger(data.transactions || []);
    } catch (error) {
      console.error('Error fetching PTO:', error);
    }
    try {
      const { data } = await axios.get(`${API}/pto/eligibility`, { withCredentials: true });
      setEligibility(data);
    } catch (error) {
      console.error('Error fetching eligibility:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (partialDay && partialStart >= partialEnd) {
      toast.error('End time must be after the start time');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: partialDay ? startDate : endDate,
        reason,
        time_off_type: timeOffType,
      };
      if (partialDay) {
        payload.partial_day = true;
        payload.partial_start = partialStart;
        payload.partial_end = partialEnd;
      }
      const { data } = await axios.post(`${API}/timeoff`, payload, { withCredentials: true });
      const excluded = data?.excluded_holiday_dates || [];
      if (excluded.length) {
        toast.success(`Request submitted. ${excluded.join(', ')} excluded — paid as company holiday, not PTO.`, { duration: 6000 });
      } else {
        toast.success('Time off request submitted');
      }
      setShowForm(false);
      setStartDate('');
      setEndDate('');
      setReason('');
      setTimeOffType('pto');
      setPaidChoice('paid');
      setPartialDay(false);
      setPartialStart('13:00');
      setPartialEnd('17:00');
      await fetchRequests();
      await fetchPto();
    } catch (error) {
      const detail = (!Array.isArray(error.response?.data?._detailMeta) && error.response?.data?._detailMeta) || error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'PTO_ON_HOLIDAY') {
        toast.error(detail.message, { duration: 7000 });
      } else {
        toast.error((detail && typeof detail === 'object' ? detail.message : detail) || 'Failed to submit request');
      }
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-500';
      case 'rejected': return 'text-red-500';
      default: return 'text-[#D4AF37]';
    }
  };

  return (
    <div data-testid="timeoff-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Time Off</h2>
          <p className="text-[#A3A3A3]">Manage your time off requests</p>
        </div>
        <Button
          data-testid="new-timeoff-button"
          onClick={() => setShowForm(!showForm)}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Button>
      </div>

      {ptoSummary && (
        <div data-testid="pto-balance-card" className="bg-gradient-to-br from-[#141414] to-[#1a1710] border border-[#D4AF37]/30 rounded-lg p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Current Available</p>
              <p className="text-2xl font-bold text-[#D4AF37]" data-testid="pto-current">{ptoSummary.current_available.toFixed(2)}h</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Pending Requests</p>
              <p className="text-2xl font-bold text-white" data-testid="pto-pending">{ptoSummary.pending_requests.toFixed(2)}h</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">After Pending Approval</p>
              <p className="text-2xl font-bold text-white" data-testid="pto-after-pending">{ptoSummary.available_after_pending.toFixed(2)}h</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">PTO Used YTD</p>
              <p className="text-2xl font-bold text-white" data-testid="pto-used-ytd">{ptoSummary.used_ytd.toFixed(2)}h</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#262626]">
            <p className="text-xs text-[#A3A3A3]">Sick used YTD: <span className="text-white">{ptoSummary.sick_used_ytd.toFixed(2)}h</span> · Sick remaining: <span className="text-green-400 font-medium">Unlimited</span></p>
            <button data-testid="pto-history-toggle" onClick={() => setShowHistory(!showHistory)} className="text-xs text-[#D4AF37] hover:text-[#F3E5AB]">{showHistory ? 'Hide' : 'View'} PTO history</button>
          </div>
          {showHistory && (
            <div data-testid="pto-ledger" className="mt-3 space-y-1 max-h-64 overflow-y-auto">
              {ptoLedger.length === 0 ? (
                <p className="text-xs text-[#A3A3A3]">No PTO transactions yet.</p>
              ) : ptoLedger.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs border-b border-[#1F1F1F] py-1.5">
                  <div>
                    <span className="text-white capitalize">{(t.transaction_type || '').replace(/_/g, ' ')}</span>
                    <span className="text-[#525252] ml-2">{t.effective_date}</span>
                    {t.reason && <span className="text-[#525252] ml-2">· {t.reason}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={t.hours < 0 ? 'text-red-400' : 'text-green-400'}>{t.hours > 0 ? '+' : ''}{Number(t.hours).toFixed(2)}h</span>
                    <span className="text-[#A3A3A3] w-16 text-right">{Number(t.balance_after).toFixed(2)}h</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {eligibility && eligibility.is_eligible_now === false && (
        <div data-testid="pto-eligibility-banner" className="rounded-lg border border-yellow-700/50 bg-yellow-900/10 p-4">
          <p className="text-yellow-300 text-sm font-medium">⏳ Your PTO benefit starts on {eligibility.eligibility_date || 'your eligibility date'}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">
            You'll receive {eligibility.annual_allowance > 0 ? `${eligibility.annual_allowance}h of PTO per year` : 'your PTO allowance'} once you become eligible (hire date + 90 days). PTO requests can't be approved before then, but sick and unpaid leave are always available.
          </p>
        </div>
      )}

      {showForm && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Full day or partial day?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="timeoff-fullday"
                  onClick={() => setPartialDay(false)}
                  className={`h-10 rounded-md border text-sm font-medium ${!partialDay ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3]'}`}
                >Full day(s)</button>
                <button
                  type="button"
                  data-testid="timeoff-partialday"
                  onClick={() => setPartialDay(true)}
                  className={`h-10 rounded-md border text-sm font-medium ${partialDay ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3]'}`}
                >Partial day (specific hours)</button>
              </div>
              {partialDay && (
                <p className="text-xs text-[#A3A3A3]">Partial time off covers a single day. Your teammates will see the exact hours you’re unavailable — never the reason or category.</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-white">{partialDay ? 'Date' : 'Start Date'}</Label>
                <Input
                  data-testid="timeoff-start-date"
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="bg-[#0A0A0A] border-[#262626] text-white"
                />
              </div>
              {!partialDay && (
                <div className="space-y-2">
                  <Label htmlFor="end-date" className="text-white">End Date</Label>
                  <Input
                    data-testid="timeoff-end-date"
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                    className="bg-[#0A0A0A] border-[#262626] text-white"
                  />
                </div>
              )}
            </div>
            {partialDay && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="partial-start" className="text-white">From</Label>
                  <Input
                    data-testid="timeoff-partial-start"
                    id="partial-start"
                    type="time"
                    value={partialStart}
                    onChange={(e) => setPartialStart(e.target.value)}
                    required
                    className="bg-[#0A0A0A] border-[#262626] text-white nf-dark-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="partial-end" className="text-white">To</Label>
                  <Input
                    data-testid="timeoff-partial-end"
                    id="partial-end"
                    type="time"
                    value={partialEnd}
                    onChange={(e) => setPartialEnd(e.target.value)}
                    required
                    className="bg-[#0A0A0A] border-[#262626] text-white nf-dark-date"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-white">Paid or Unpaid?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="timeoff-paid"
                  onClick={() => { setPaidChoice('paid'); if (timeOffType === 'unpaid') setTimeOffType('pto'); }}
                  className={`h-10 rounded-md border text-sm font-medium ${paidChoice === 'paid' ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3]'}`}
                >Paid</button>
                <button
                  type="button"
                  data-testid="timeoff-unpaid"
                  onClick={() => { setPaidChoice('unpaid'); setTimeOffType('unpaid'); }}
                  className={`h-10 rounded-md border text-sm font-medium ${paidChoice === 'unpaid' ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3]'}`}
                >Unpaid</button>
              </div>
              <p className="text-xs text-[#A3A3A3]">You’re requesting this status — the office confirms and approves your paid hours for each day.</p>
            </div>
            {paidChoice === 'paid' && (
              <div className="space-y-2">
                <Label htmlFor="timeoff-type" className="text-white">Category</Label>
                <select
                  data-testid="timeoff-type"
                  id="timeoff-type"
                  value={timeOffType}
                  onChange={(e) => setTimeOffType(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md h-10 px-3 text-white text-sm"
                >
                  <option value="pto">PTO</option>
                  <option value="sick">Sick</option>
                  <option value="bereavement">Bereavement</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-white">Reason</Label>
              <Textarea
                data-testid="timeoff-reason"
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                rows={3}
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="timeoff-submit-button"
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                Submit Request
              </Button>
              <Button
                type="button"
                onClick={() => setShowForm(false)}
                variant="outline"
                className="border-[#262626] text-white hover:bg-[#262626]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-4">
        {requests.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">
            No time off requests yet
          </div>
        ) : (
          requests.map((request, idx) => (
            <div key={idx} className="bg-[#141414] border border-[#262626] rounded-md p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white font-medium">
                      {request.start_date} - {request.end_date}
                    </span>
                    {request.time_off_type && (
                      <span className="px-2 py-0.5 rounded text-[11px] bg-[#262626] text-[#A3A3A3] uppercase tracking-wider">{request.time_off_type}</span>
                    )}
                  </div>
                  <p className="text-[#A3A3A3] ml-8">{request.reason}</p>
                  {request.status === 'approved' && typeof request.total_paid_hours === 'number' && (
                    <p className="text-green-400 text-sm ml-8 mt-1" data-testid={`timeoff-paid-${request.id}`}>{request.total_paid_hours.toFixed(2)} paid leave hours approved</p>
                  )}
                  {request.has_conflict && (
                    <p className="text-orange-400 text-xs ml-8 mt-1">⚠ Approved over a schedule conflict (override)</p>
                  )}
                </div>
                <div className={`px-3 py-1 rounded-md text-sm font-medium ${getStatusColor(request.status)} bg-[#262626]`}>
                  {request.status.toUpperCase()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TimeOff;